import React, { useState, useEffect } from 'react';
import { useAuth } from '../../services/contexts/AuthContext';
import { patientsAPI } from '../../services/api/api';
import { 
  Plus, 
  Send, 
  Mail, 
  MessageSquare, 
  Activity,
  Search,
  Filter,
  Users,
  Calendar,
  Eye,
  Edit,
  Phone,
  MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';
import AddPatientModal from '../AddPatientModal';
import PatientVitalsModal from '../PatientVitalsModal';

const PatientsView = () => {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showVitals, setShowVitals] = useState(false);
  const [sendingLink, setSendingLink] = useState(null);
  const [editingPatient, setEditingPatient] = useState(null);

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const response = await patientsAPI.getPatients();
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPatient = async (patientData) => {
    try {
      if (patientData.patient_id) {
        // Editing existing patient
        await patientsAPI.updatePatient(patientData.patient_id, patientData);
        toast.success('Patient updated successfully!');
        setEditingPatient(null);
      } else {
        // Adding new patient
        await patientsAPI.createPatient(patientData);
        toast.success('Patient added successfully!');
        setShowAddPatient(false);
      }
      loadPatients();
    } catch (error) {
      toast.error(patientData.patient_id ? 'Failed to update patient' : 'Failed to add patient');
    }
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setShowAddPatient(true);
  };

  const handleSendLink = async (patientId, channel) => {
    // This function is deprecated - use role-specific APIs instead
    toast.error('Please use the role-specific patient management interface to send recording links');
  };

  const handleViewVitals = (patient) => {
    setSelectedPatient(patient);
    setShowVitals(true);
  };

  const filteredPatients = patients.filter(patient =>
    patient.alias.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getViewTitle = () => {
    switch (user?.role) {
      case 'system_admin':
        return 'Global Patient Management';
      case 'clinic_head':
        return 'Clinic Patient Overview';
      case 'clinic_technician':
        return 'My Assigned Patients';
      case 'patient':
        return 'My Health Records';
      default:
        return 'Patient Management';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{getViewTitle()}</h2>
          <p className="text-gray-600 mt-1">
            {user?.role === 'patient' 
              ? 'View and manage your health information'
              : `Manage and monitor patient vital signs and recordings`
            }
          </p>
        </div>

        {user?.role !== 'patient' && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowAddPatient(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Patient
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{filteredPatients.length}</h3>
              <p className="text-gray-600 text-sm">
                {user?.role === 'patient' ? 'Health Records' : 'Total Patients'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">12</h3>
              <p className="text-gray-600 text-sm">Active Recordings</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">8</h3>
              <p className="text-gray-600 text-sm">Scheduled Today</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Send className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">5</h3>
              <p className="text-gray-600 text-sm">Pending Links</p>
            </div>
          </div>
        </div>
      </div>

      {/* Patients Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">
              {user?.role === 'patient' ? 'Health Records' : 'Patient List'}
            </h3>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search patients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Filter */}
              <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <Filter className="h-4 w-4 mr-2 text-gray-500" />
                Filter
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant & Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Demographics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Recording
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm ? 'No patients found matching your search.' : 'No patients added yet.'}
                  </td>
                </tr>
              ) : (
                filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {patient.alias.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{patient.alias}</div>
                          <div className="text-sm text-gray-500">ID: {patient.id.substring(0, 8)}...</div>
                          <div className="text-xs text-gray-400">
                            Created: {new Date(patient.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {/* Tenant Information */}
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                            <span className="text-green-600 text-xs font-bold">T</span>
                          </div>
                          <span className="font-medium text-green-700">{patient.tenant_name}</span>
                        </div>
                        
                        {/* Contact Information */}
                        <div className="flex items-center mb-1">
                          <Mail className="h-3 w-3 text-gray-400 mr-1" />
                          <span className="truncate max-w-[150px]">{patient.email}</span>
                        </div>
                        <div className="flex items-center mb-1">
                          <Phone className="h-3 w-3 text-gray-400 mr-1" />
                          <span>{patient.phone}</span>
                        </div>
                        {patient.address && (
                          <div className="flex items-center">
                            <MapPin className="h-3 w-3 text-gray-400 mr-1" />
                            <span className="text-xs text-gray-500 truncate max-w-[120px]">{patient.address}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {patient.gender && <span className="capitalize">{patient.gender}</span>}
                        {patient.dob && patient.gender && <span className="mx-1">•</span>}
                        {patient.dob && (
                          <span>
                            {new Date().getFullYear() - new Date(patient.dob).getFullYear()} years
                          </span>
                        )}
                      </div>
                      {patient.dob && (
                        <div className="text-sm text-gray-500">
                          DOB: {new Date(patient.dob).toLocaleDateString()}
                        </div>
                      )}
                      {patient.emergency_contact_name && (
                        <div className="text-xs text-gray-400">
                          Emergency: {patient.emergency_contact_name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="text-sm text-gray-900">3 days ago</div>
                      <div className="text-sm text-gray-500">Heart Rate: 72 BPM</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewVitals(patient)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                          title="View Vitals"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {user?.role !== 'patient' && (
                          <>
                            <button
                              onClick={() => handleEditPatient(patient)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-colors"
                              title="Edit Patient"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => handleViewVitals(patient)}
                              className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded transition-colors"
                              title="View Vitals History"
                            >
                              <Activity className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => handleSendLink(patient.id, 'email')}
                              disabled={sendingLink === patient.id}
                              className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Send Email Link"
                            >
                              {sendingLink === patient.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                              ) : (
                                <Mail className="h-4 w-4" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => handleSendLink(patient.id, 'sms')}
                              disabled={sendingLink === patient.id}
                              className="text-orange-600 hover:text-orange-900 p-1 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                              title="Send SMS Link"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showAddPatient && (
        <AddPatientModal
          patient={editingPatient}
          onClose={() => {
            setShowAddPatient(false);
            setEditingPatient(null);
          }}
          onSubmit={handleAddPatient}
        />
      )}
      
      {showVitals && selectedPatient && (
        <PatientVitalsModal
          patient={selectedPatient}
          onClose={() => {
            setShowVitals(false);
            setSelectedPatient(null);
          }}
        />
      )}
    </div>
  );
};

export default PatientsView;